import { DependencyList, useContext, useEffect, useRef } from 'react';
import { IntersectionObserverContext } from '../context/IntersectionObserverContext';

export const VISIBILITY_THRESHOLD = 0.8;

/**
 * Simple hook to set up an intersection observer. A type must be passed in
 * order to properly create the target observable element ref and the
 * intersectionCallback will receive whether or not that element is
 * currently intersecting with the root.
 * Use an IntersectionObserverProvider to overwrite the observer's root,
 * otherwise, the default viewport will be used.
 *
 * Example implementation:
 *
 * const MyComponent = () => {
 *   const ref = useIntersectionObserver<HTMLDivElement>(
 *     (isIntersecting: boolean) => {
 *       console.log(`Is MyComponent in the viewport?: ${isIntersecting}`);
 *     }
 *   );
 *
 *   return (
 *     <div ref={ref}>MyComponent</div>
 *   );
 * }
 */
function useIntersectionObserver<T>(
  intersectionCallback: (isIntersecting: boolean) => void,
  threshold = VISIBILITY_THRESHOLD,
  dependencies: DependencyList = []
) {
  /**
   * By default, try to use the root defined by an intersection observer provider,
   * which allows to overwrite the observer's root target. If there's not a
   * provider higher in the hierarchy and the root is undefined, then it defaults
   * to the whole viewport.
   */
  const { root } = useContext(IntersectionObserverContext);
  /**
   * Creating the ref that will be returned to the implementor component, which
   * must be assigned to the desired observable element complying with the
   * generic `T` type.
   */
  const ref = useRef<T>(null);

  useEffect(() => {
    if (!ref?.current) {
      return;
    }

    const observer = new IntersectionObserver(onIntersectionChange, {
      root: root?.current,
      threshold,
    });

    observer.observe(ref.current as never as Element);

    return () => {
      observer.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref, ...dependencies]);

  const onIntersectionChange = (entries: IntersectionObserverEntry[]) => {
    entries.forEach((entry: IntersectionObserverEntry) => {
      const { isIntersecting } = entry;
      intersectionCallback(isIntersecting);
    });
  };

  return ref;
}

export default useIntersectionObserver;
